import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Clock, Check, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { toast } from 'sonner';
import { API_BASE as API } from '../lib/apiBase';
import { useLegalGate } from '../components/LegalAcceptanceModal';

export default function InquireOfferPage() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { lang } = useLang();
  const legalInquire = useLegalGate('create_booking_inquire');
  const legalFormal = useLegalGate('create_booking_reservation');
  const [o, setO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proceeding, setProceeding] = useState(false);
  const [proceedOn, setProceedOn] = useState(false);
  const [aMsg, setAMsg] = useState('');
  const [aPrice, setAPrice] = useState('');
  const [bMsg, setBMsg] = useState('');
  const [bPrice, setBPrice] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    const r = await fetch(`${API}/api/booking-offers/${offerId}`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    if (r.ok) setO(d);
    else toast.error(d.detail || 'Error');
    setLoading(false);
  }, [token, offerId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetch(`${API}/api/admin/settings/public`)
      .then((r) => r.json())
      .then((d) => setProceedOn(!!d.BOOKING_PROCEED_TO_FORMAL))
      .catch(() => setProceedOn(false));
  }, []);

  if (!token) {
    return (
      <div className="min-h-screen pt-20 px-4 text-center text-pulse-muted">
        <Link to="/login" className="text-pulse-primary">Login</Link>
      </div>
    );
  }
  if (loading || !o) {
    return <div className="min-h-screen pt-14 flex justify-center p-8"><div className="w-8 h-8 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  const uid = user?.id;
  const isArtist = uid === o.artist_user_id;
  const isBusiness = uid === o.business_user_id;
  const toCents = (s) => (s === '' || s == null ? null : Math.round(parseFloat(String(s).replace(',', '.'), 10) * 100) || null);

  const doArtistPropose = async () => {
    if (!legalInquire.gate()) return;
    const r = await fetch(`${API}/api/booking-offers/${offerId}/artist/propose`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ message: aMsg, price_cents: toCents(aPrice) }),
    });
    const d = await r.json();
    if (r.ok) { toast.success(lang === 'es' ? 'Enviada' : 'Sent'); setO(d); setAMsg(''); setAPrice(''); } else { toast.error(typeof d.detail === 'object' ? d.detail?.message : d.detail); }
  };
  const doArtistReject = async () => {
    if (!legalInquire.gate()) return;
    const r = await fetch(`${API}/api/booking-offers/${offerId}/artist/reject`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    if (r.ok) { toast.success(lang === 'es' ? 'Cerrada' : 'Closed'); setO(d); } else { toast.error(d.detail); }
  };
  const doBusiness = async (accept) => {
    if (!legalInquire.gate()) return;
    const r = await fetch(`${API}/api/booking-offers/${offerId}/business/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ accept, message: bMsg, price_cents: accept ? undefined : toCents(bPrice) }),
    });
    const d = await r.json();
    if (r.ok) { toast.success(accept ? (lang === 'es' ? 'Aceptado' : 'Accepted') : (lang === 'es' ? 'Contraoferta' : 'Counter sent')); setO(d); } else { toast.error(typeof d.detail === 'string' ? d.detail : 'Error'); }
  };
  const doArtistFinal = async (acc) => {
    if (!legalInquire.gate()) return;
    const r = await fetch(`${API}/api/booking-offers/${offerId}/artist/final`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ accept: acc }),
    });
    const d = await r.json();
    if (r.ok) { setO(d); } else { toast.error(d.detail); }
  };

  const doProceedToFormal = async () => {
    if (!isBusiness) return;
    if (!legalFormal.gate()) return;
    setProceeding(true);
    const r = await fetch(`${API}/api/booking-offers/${offerId}/proceed-to-formal`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = await r.json();
    setProceeding(false);
    if (r.ok) {
      toast.success(lang === 'es' ? 'Reserva formal creada' : 'Formal booking created');
      load();
      setTimeout(() => { navigate('/bookings'); }, 500);
    } else {
      const msg = typeof d.detail === 'string' ? d.detail : (d.detail?.message || (lang === 'es' ? 'Error' : 'Error'));
      toast.error(msg);
    }
  };

  return (
    <div data-testid="inquire-offer-page" className="min-h-screen pt-14 pb-20 px-4 max-w-2xl mx-auto">
      {legalInquire.legalModal}
      {legalFormal.legalModal}
      <Link to="/profile" className="text-pulse-muted text-sm flex items-center gap-1 mb-4"><ArrowLeft size={16} /> {lang === 'es' ? 'Volver' : 'Back'}</Link>
      <h1 className="font-heading text-xl font-bold flex items-center gap-2">
        <MessageSquare size={20} className="text-pulse-primary" />
        {lang === 'es' ? 'Consulta (precontrato)' : 'Inquiry (pre-contract)'}
      </h1>
      {o.current_deadline && o.status && ['open', 'awaiting_business', 'countered'].includes(o.status) && (
        <p className="text-xs text-pulse-muted flex items-center gap-1 mt-1"><Clock size={12} /> 48h — {o.current_deadline?.slice(0, 19)}Z</p>
      )}
      <div className="mt-4 rounded-2xl border border-pulse-border bg-pulse-surface p-4 text-sm space-y-2">
        <p><span className="text-pulse-muted">Artista:</span> {o.artist_name}</p>
        <p><span className="text-pulse-muted">Estado:</span> {o.status} / {o.awaiting}</p>
        <p><span className="text-pulse-muted">{lang === 'es' ? 'Evento' : 'Event'}:</span> {o.event_type} | {o.date} | {o.location}</p>
        <p className="whitespace-pre-wrap text-pulse-muted">{o.initial_message}</p>
        {o.artist_proposal && <div className="border-t border-pulse-border pt-2"><span className="text-cyan-300">{lang === 'es' ? 'Propuesta del artista' : 'Artist proposal'}</span><p className="whitespace-pre-wrap mt-1">{o.artist_proposal.message}</p><p className="text-pulse-primary">{o.artist_proposal.price_cents != null ? `${(o.artist_proposal.price_cents / 100).toFixed(0)} €` : '—'}</p></div>}
        {o.business_counter && <div className="border-t border-pulse-border pt-2"><span className="text-amber-300">{lang === 'es' ? 'Contraoferta' : 'Counter'}</span><p className="whitespace-pre-wrap mt-1">{o.business_counter.message}</p><p className="text-amber-200">{o.business_counter.price_cents != null ? `${(o.business_counter.price_cents / 100).toFixed(0)} €` : '—'}</p></div>}
      </div>

      {o.status === 'open' && isArtist && (
        <div className="mt-6 space-y-3">
          <textarea className="w-full rounded-xl border border-pulse-border bg-pulse-elevated p-3 text-sm" value={aMsg} onChange={(e) => setAMsg(e.target.value)} rows={3} placeholder={lang === 'es' ? 'Tu propuesta (condiciones y presupuesto)' : 'Your proposal and budget'} />
          <input className="w-full rounded-xl border border-pulse-border bg-pulse-elevated p-3 text-sm" value={aPrice} onChange={(e) => setAPrice(e.target.value)} placeholder="€ (importe en euros, opcional)" inputMode="decimal" />
          <div className="flex gap-2">
            <button onClick={doArtistPropose} className="flex-1 bg-pulse-primary text-black font-semibold py-2 rounded-xl text-sm">{lang === 'es' ? 'Enviar propuesta' : 'Send proposal'}</button>
            <button onClick={doArtistReject} className="px-3 py-2 rounded-xl border border-pulse-border text-pulse-muted text-sm">{lang === 'es' ? 'No' : 'Decline'}</button>
          </div>
        </div>
      )}

      {o.status === 'awaiting_business' && o.awaiting === 'business' && isBusiness && (
        <div className="mt-6 space-y-3">
          <button onClick={() => doBusiness(true)} className="w-full flex items-center justify-center gap-2 bg-pulse-primary text-black font-bold py-3 rounded-xl text-sm">
            <Check size={16} /> {lang === 'es' ? 'Aceptar propuesta' : 'Accept proposal'}
          </button>
          <p className="text-xs text-pulse-muted text-center">— {lang === 'es' ? 'o contraofertar' : 'or counter'} —</p>
          <textarea className="w-full rounded-xl border border-pulse-border bg-pulse-elevated p-3 text-sm" value={bMsg} onChange={(e) => setBMsg(e.target.value)} rows={2} placeholder="Mensaje" />
          <input className="w-full rounded-xl border border-pulse-border bg-pulse-elevated p-3 text-sm" value={bPrice} onChange={(e) => setBPrice(e.target.value)} placeholder="€ contrapropuesta" />
          <button onClick={() => doBusiness(false)} className="w-full border border-pulse-border py-2 rounded-xl text-sm" disabled={!bMsg.trim()}><X size={14} className="inline" /> {lang === 'es' ? 'Contraofertar' : 'Send counter'}</button>
        </div>
      )}

      {o.status === 'countered' && isArtist && (
        <div className="mt-6 flex gap-2">
          <button onClick={() => doArtistFinal(true)} className="flex-1 bg-pulse-primary text-black font-bold py-3 rounded-xl text-sm"><Check size={16} /> {lang === 'es' ? 'Aceptar' : 'Accept'}</button>
          <button onClick={() => doArtistFinal(false)} className="flex-1 border border-pulse-border py-3 rounded-xl text-sm"><X size={16} /> {lang === 'es' ? 'Rechazar' : 'Reject'}</button>
        </div>
      )}

      {o.status === 'closed_accepted' && (
        <div className="mt-6 space-y-4">
          <div className="rounded-2xl border border-green-500/25 bg-green-500/10 p-4 text-sm text-green-200/95">
            <p className="font-medium text-white mb-1">
              {lang === 'es' ? 'Alineación de intención' : 'Aligned intent'}
            </p>
            <p>
              {lang === 'es'
                ? 'La fase de consulta ha terminado. El siguiente paso, si lo activa la plataforma, es crear la ficha de reserva en el flujo de contratación. El cobro no se ejecuta automáticamente con este botón.'
                : 'Inquiry is closed. If enabled, you can create a formal booking record. Payment is not taken automatically from this action.'}
            </p>
          </div>
          {o.linked_booking_id && (
            <p className="text-xs text-pulse-muted">
              {lang === 'es' ? 'Ficha vinculada' : 'Linked record'}:{' '}
              <code className="text-cyan-300/90">{o.linked_booking_id.slice(0, 8)}…</code>
              <Link to="/bookings" className="ml-2 text-pulse-primary font-medium">
                {lang === 'es' ? 'Ver reservas' : 'View bookings'}
              </Link>
            </p>
          )}
          {proceedOn && isBusiness && !o.linked_booking_id && o.agreed_price_cents > 0 && (
            <div>
              <button
                type="button"
                disabled={proceeding}
                onClick={doProceedToFormal}
                className="w-full py-3 rounded-xl bg-pulse-primary text-black font-bold text-sm disabled:opacity-50"
              >
                {proceeding
                  ? (lang === 'es' ? 'Guardando…' : 'Saving…')
                  : (lang === 'es' ? 'Continuar a contratación' : 'Continue to booking')}
              </button>
              <p className="text-[10px] text-pulse-muted mt-2 text-center">
                {lang === 'es'
                  ? 'Acepta los términos de contratación de la plataforma si aún no los tienes. El pago (wallet u otro) queda en el flujo de la reserva.'
                  : 'You will accept formal booking terms if still needed. Payment stays in the booking flow, not on this page.'}
              </p>
            </div>
          )}
          {proceedOn && isBusiness && !o.linked_booking_id && !(o.agreed_price_cents > 0) && (
            <p className="text-xs text-amber-200/90">
              {lang === 'es'
                ? 'Falta un importe acordado en el sistema; no se puede generar aún la ficha de reserva. Acuerda un importe o contacta a soporte.'
                : 'No agreed amount on record; cannot create a formal booking line yet.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
